import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper from '../chart_helpers/AxisHelper'
import { continuousExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import SizeHelper from '../chart_helpers/SizeHelper'

import { extent as d3_extent } from 'd3-array'
import { scaleLinear as d3_scaleLinear } from 'd3-scale'
import { select as d3_select } from 'd3-selection'
import { wrapSvgText } from '../utils/textUtils'
import { getBrightness } from '../utils/colorUtils'
import { interpolate as d3_interpolate, interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import {
  drawSvgCircle,
  drawSvgSquare,
  drawSvgTriangle,
  drawSvgStar,
  drawSvgPolygon,
  drawSvgPlus,
} from '../utils/shapeUtils'

const DEFAULT_SCATTER_R = 6
export default class ScatterPlotChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    groupAttr: {
      type: String,
      default: 'group',
    },
    xAttr: {
      type: String,
      default: 'x',
    },
    yAttr: {
      type: String,
      default: 'y',
    },
    sizeAttr: {
      type: [String, null],
      default: 'size',
    },
    mode: {
      type: [String],
      default: 'dots',
      options: ['dots', 'grid'],
    },
    /**
     * {-EN-} If set, all data points will have at least this radius.
     * {-ES-} Si se establece, todos los puntos de datos tendrán al menos este radio.
     */
    minSize: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * {-EN-} If set, all data points will have at most this radius.
     * {-ES-} Si se establece, todos los puntos de datos tendrán como máximo este radio.
     */
    maxSize: {
      type: Number,
      default: Infinity,
      exposed: true,
    },
    /**
     * {-EN-} Show/hide the label of each data point.
     * {-ES-} Mostrar/ocultar la etiqueta de cada punto de datos.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    AxisHelper: {
      zoom: {
        default: false,
        exposed: true,
      },
      scroll: {
        nonEditable: true,
      },
      showScrollbar: {
        default: true,
      },
      x: {
        scale: 'linear',
        getValue: function (d) {
          return d.x
        },
        getExtent: function (data) {
          let extent = continuousExtent(data, d => d[this.config.xAttr]) // d3_extent(data.map((d)=>d[this.config.xAttr]))
          // return padLinear(extent,0.2)
          return extent
        },
        grid: true,
      },
      y: {
        scale: 'linear',
        getValue: function (d) {
          return d.y
        },
        getExtent: function (data) {
          let extent = continuousExtent(data, d => d[this.config.yAttr]) //d3_extent(data.map((d)=>d[this.config.yAttr]))
          // return padLinear(extent,0.2)
          return extent
        },
        grid: true,
      },
    },
    hasColorPatterns: {
      type: Boolean,
      default: false,
      exposed: false,
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  checkConsistency() {
    return true
  }
  getLegendData() {
    let allData = this.allData || this.data
    let legendData = {}
    if (this.config.groupAttr) {
      let groups = allData.reduce((res, d) => {
        res.push(d.group)
        return res
      }, [])
      groups = groups.filter((d, i) => groups.indexOf(d) === i)
      let groupData = groups.map(g => {
        return { id: g, group: g }
      })
      legendData.group = groupData
      legendData.group.icon = 'circle'
    }

    if (this.config.sizeAttr) {
      let sizes = this._sizeScale.nice().ticks(3)
      sizes = [sizes[0], sizes[sizes.length - 1]]
      sizes.splice(1, 0, sizes[0] + (sizes[1] - sizes[0]) / 2)

      let sizeData = sizes.map(d => ({ id: d, value: 2 * this._sizeScale(d) }))

      legendData.size = sizeData
      legendData.size.icon = 'circle'
      legendData.size.type = 'size'
    }
    return legendData
  }
  beforeHelpers() {
    this._sizeScale = d3_scaleLinear()
      .domain(d3_extent(this.data, d => d.size))
      .range([3, 15])
  }
  draw() {
    const self = this

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    if (this.config.mode === 'grid') {
      this.itemsEnter
        .selectAll('.square')
        .data([null])
        .enter()
        .append('g')
        .attr('class', 'square')
        .append('rect')
      // this.itemsEnter.call(drawItem,'enter')
      this.itemsEnter
        .select('.square')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('rect')
        .attr('fill', d => this.color(d))
        .attr('width', () => this.axes.x.func.scale().bandwidth())
        .attr('height', () => this.axes.y.func.scale().bandwidth())
        .style('opacity', 0)

      this.itemsEnterTrans.select('.square').select('rect').style('opacity', 1)
      this.itemsTrans
        .select('.square')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('rect')
        .attr('fill', d => this.color(d))
        .attr('width', () => this.axes.x.func.scale().bandwidth())
        .attr('height', () => this.axes.y.func.scale().bandwidth())
        .style('opacity', 1)

      let mergedItems = this.items.merge(this.itemsEnter)
      let labels = mergedItems
        .select('.square')
        .selectAll('text')
        .data(this.config.showLabels ? [null] : [])
      labels
        .enter()
        .append('text')
        .attr('font-size', '14px')
        .attr('fill', 'currentColor')
        .attr('text-anchor', 'middle')
        .style('dominant-baseline', 'middle')
        .style('opacity', 0)
        .attr(
          'transform',
          () =>
            `translate(${this.axes.x.func.scale().bandwidth() / 2},${
              this.axes.y.func.scale().bandwidth() / 2
            })`
        )

      labels.exit().transition().style('opacity', 0).remove()

      this.itemsEnter
        .select('.square')
        .select('text')

        .property('_current', d => d.color)
        .text(d => this.valueWithUnit(d.color))
        .attr('fill', d => {
          let brightness = getBrightness(this.color(d))
          return brightness < 140 ? '#fff' : '#000'
        })
      this.itemsEnterTrans.select('.square').select('text').style('opacity', 1)
      this.itemsTrans
        .select('.square')
        .select('text')
        .style('opacity', 1)
        .attr(
          'transform',
          () =>
            `translate(${this.axes.x.func.scale().bandwidth() / 2},${
              this.axes.y.func.scale().bandwidth() / 2
            })`
        )
        .attr('fill', d => {
          let brightness = getBrightness(this.color(d))
          return brightness < 140 ? '#fff' : '#000'
        })
        .textTween(function (d) {
          const i = d3_interpolate(this._current || d.color, d.color)
          return function (t) {
            return self.valueWithUnit((this._current = i(t)))
          }
        })
      this.itemsExitTrans.style('opacity', 0)
    } else {
      let safeR = d => {
        let max = this.config.maxSize ? this.config.maxSize : Infinity
        let min = this.config.minSize ? this.config.minSize : 0
        return this.config.sizeAttr
          ? Math.max(min, Math.min(max, this._sizeScale(d.size)))
          : DEFAULT_SCATTER_R
      }

      let points = this.itemsEnter.selectAll('.point').data(d => [d])
      const pointsEnter = points.enter().append('g').attr('class', 'point')
      points.exit().remove()
      points = points.merge(pointsEnter)

      points.attr(
        'transform',
        d =>
          `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
            d[this.config.yAttr]
          )})`
      )

      const getPointType = d => {
        if (
          !this.config.ItemsHelper.colorDict ||
          !this.config.ItemsHelper.colorDict[d.group]?.shape
        )
          return 'circle'
        return this.config.ItemsHelper.colorDict[d.group].shape
      }

      let mergedItems = this.items.merge(this.itemsEnter)

      const drawShapes = (className, shapeType, drawEnterCb, drawMergeCb) => {
        let shapes = mergedItems
          .select('.point')
          .selectAll(`.${className}`)
          .data(
            d => {
              return getPointType(d) === shapeType ? [d] : []
            },
            d => d.id
          )

        const shapesEnter = shapes
          .enter()
          .append('g')
          .attr('class', `point-shape ${className}`)
          .attr('stroke-width', 0)
        shapesEnter.call(drawEnterCb)
        shapes.exit().remove()

        shapes = shapes.merge(shapesEnter)
        shapes
          //.attr('fill', d => this.color(d))
          .attr('stroke', d => this.color(d))
          .call(drawMergeCb)

        this.itemsEnterTrans
          .select('.point')
          .select(`.${className}`)
          .attrTween('fill', fillTween)
          .attr('stroke', d => this.color(d))
          .call(drawMergeCb)

        this.itemsTrans.select('.point').select(`.${className}`).attrTween('fill', fillTween)
      }

      const SHAPES_DEFAULT_SIZE = 12

      //Circle
      drawShapes(
        'circle',
        'circle',
        sel => drawSvgCircle(sel, 0, 'enter'),
        sel => drawSvgCircle(sel, safeR, 'edit')
      )

      //Square
      drawShapes(
        'square',
        'square',
        sel => drawSvgSquare(sel, 0, 'enter'),
        sel => drawSvgSquare(sel, SHAPES_DEFAULT_SIZE, 'edit')
      )
      //Triangles
      drawShapes(
        'triangle',
        'triangle',
        sel => drawSvgTriangle(sel, 0, 'enter'),
        sel => drawSvgTriangle(sel, SHAPES_DEFAULT_SIZE, 'edit')
      )

      //Five point star
      drawShapes(
        'five-point-star',
        'fivePointStar',
        sel => drawSvgStar(sel, 0, 5, 'enter'),
        sel => drawSvgStar(sel, SHAPES_DEFAULT_SIZE, 5, 'edit')
      )

      //Six point star
      drawShapes(
        'six-point-star',
        'sixPointStar',
        sel => drawSvgStar(sel, 0, 6, 'enter'),
        sel => drawSvgStar(sel, SHAPES_DEFAULT_SIZE, 6, 'edit')
      )

      //Hexagon
      drawShapes(
        'hexagon',
        'hexagon',
        sel => drawSvgPolygon(sel, 0, 6, 'enter'),
        sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE, 6, 'edit')
      )

      //Pentagon
      drawShapes(
        'pentagon',
        'pentagon',
        sel => drawSvgPolygon(sel, 0, 5, 'enter'),
        sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE, 5, 'edit')
      )

      //Plus
      drawShapes(
        'plus',
        'plus',
        sel => drawSvgPlus(sel, 0, 'enter'),
        sel => drawSvgPlus(sel, SHAPES_DEFAULT_SIZE, 'edit')
      )

      //Labels
      let labels = mergedItems
        .select('.point')
        .selectAll('text')
        .data(this.config.showLabels ? [null] : [])
      labels
        .enter()
        .append('text')
        // .attr('text-anchor','middle')
        .attr('font-size', '14px')
        .attr('fill', 'currentColor')
      labels.exit().transition().style('opacity', 0).remove()

      mergedItems
        .select('.point')
        .select('text')
        .text(d => d.id)
        .call(wrapSvgText, 80)
        .attr('transform', d => `translate(${safeR(d) + 6},-${safeR(d) + 6})`)
        .selectAll('tspan')
        .attr('x', 0)
        .attr('y', 0)

      this.itemsEnter
        .select('.point')
        //  .select('text')
        .style('opacity', 0)
      this.itemsEnterTrans
        .select('.point')
        // .select('text')
        .style('opacity', 1)

      const pointItemsTrans = this.itemsTrans
        .select('.point')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .style('opacity', 1)

      /*

      //Circle
      pointItemsTrans
        .select('.circle')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgCircle(sel, safeR, 'edit'))

      //Square
      pointItemsTrans
        .select('.square')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgSquare(sel, SHAPES_DEFAULT_SIZE, 'edit'))

      //Triangle
      pointItemsTrans
        .select('.triangle')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgTriangle(sel, SHAPES_DEFAULT_SIZE, 'edit'))

      //Five point star
      pointItemsTrans
        .select('.five-point-star')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgStar(sel, SHAPES_DEFAULT_SIZE, 5, 'edit'))

      //Six point star
      pointItemsTrans
        .select('.six-point-star')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgStar(sel, SHAPES_DEFAULT_SIZE, 6, 'edit'))

      //Hexagon
      pointItemsTrans
        .select('.hexagon')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE, 6, 'edit'))

      //Pentagon
      pointItemsTrans
        .select('.pentagon')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE, 5, 'edit'))

      //Plus
      pointItemsTrans
        .select('.plus')
        .attr('fill', d => this.color(d))
        .attr('stroke', d => this.color(d))
        .call(sel => drawSvgPlus(sel, SHAPES_DEFAULT_SIZE, 'edit'))
      */

      this.itemsExitTrans.style('opacity', 0)
    }
  }
}
